/**
 * OpenThreadPicker — "Add an open thread…" on a meeting (Matt, 2026-10-19). The agenda fills itself
 * from who is involved (api/_meetingAgenda.js); this is the by-hand path for anything else: pick
 * existing open threads on the project to put on this meeting's agenda.
 *
 * openThreadPicker({ threads }) → Promise<string[] | null>   threads: [{ id, title, type, court }]
 * Mounts its own root on <body> (opened from menus in two places).
 */
import { useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'

const font = '"DM Sans", sans-serif'

function Picker({ threads, onDone }) {
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState(() => new Set())
  const shown = useMemo(() => {
    const toks = q.toLowerCase().split(/\s+/).filter(Boolean)
    return threads.filter(t => toks.every(k => (t.title + ' ' + (t.type || '')).toLowerCase().includes(k)))
  }, [q, threads])
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onDone(null) }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onDone])
  const toggle = id => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onDone(null) }}
      style={{ position: 'fixed', inset: 0, zIndex: 2147483000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font }}>
      <div style={{ width: 'min(520px, calc(100vw - 32px))', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated, #fff)', color: 'var(--aj-text, #1d1d1f)', borderRadius: 12, border: '0.5px solid var(--aj-border-mid, rgba(0,0,0,.12))', boxShadow: '0 18px 48px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '16px 18px 10px' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Add open threads to this meeting</div>
          <div style={{ fontSize: 13, color: 'var(--aj-text-mid, #3a3a3c)', marginTop: 4 }}>They join the agenda and show their history from earlier meetings.</div>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Find a thread…"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '8px 10px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid, #ccc)', background: 'var(--aj-bg-page, #f4f7f8)', color: 'inherit', fontFamily: font, fontSize: 13 }} />
        </div>
        <div style={{ overflowY: 'auto', padding: '0 10px', flex: 1, minHeight: 100 }}>
          {!shown.length && <div style={{ fontSize: 12, color: 'var(--aj-text-muted, #6e6e73)', padding: 8 }}>{threads.length ? 'Nothing matches.' : 'Every open thread on this project is already on the agenda.'}</div>}
          {shown.map(t => {
            const on = picked.has(t.id)
            return (
              <label key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', margin: '2px 0', borderRadius: 6, cursor: 'pointer', background: on ? 'rgba(7,39,45,0.08)' : 'transparent' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(t.id)} style={{ marginTop: 3 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{t.title}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--aj-text-muted, #6e6e73)' }}>{[t.type, t.court ? 'In ' + t.court + '’s court' : null].filter(Boolean).join(' · ')}</span>
                </span>
              </label>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '0.5px solid var(--aj-border, rgba(0,0,0,.08))' }}>
          <button onClick={() => onDone(null)} style={{ padding: '8px 14px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid, #ccc)', background: 'transparent', color: 'inherit', fontFamily: font, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button disabled={!picked.size} onClick={() => onDone([...picked])} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: picked.size ? '#07272D' : 'var(--aj-bg-muted, #ccc)', color: '#fff', fontFamily: font, fontSize: 13, fontWeight: 600, cursor: picked.size ? 'pointer' : 'default' }}>
            {picked.size ? 'Add ' + picked.size + ' to the agenda' : 'Pick threads'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function openThreadPicker({ threads }) {
  return new Promise(resolve => {
    const host = document.createElement('div')
    document.body.appendChild(host)
    const root = createRoot(host)
    const done = v => { root.unmount(); host.remove(); resolve(v) }
    root.render(<Picker threads={threads || []} onDone={done} />)
  })
}
